import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import type { GridInitialStatePro } from '@mui/x-data-grid-pro/models/gridStatePro';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { buildQueryString } from '../../utils/buildQueryString';
import { useDataGridInitialState } from '../useDataGridInitialState/useDataGridInitialState';

/** The URL params `buildQueryString` owns, in the order it emits them. */
const DATA_GRID_SEARCH_PARAMS = [
  'page',
  'pageSize',
  'sort',
  'filter',
  'logicOperator',
  'quickFilter',
  'quickFilterValues',
  'quickFilterLogicOperator',
];

export const useDataGridState = (
  disableSearchParamSync: boolean,
  pageSizeOptions: readonly (number | { value: number; label: string })[],
  initialState: GridInitialStatePro | undefined,
  {
    pagination,
    initialPageSize,
    preventUrlReadWhenSyncDisabled = false,
    resetPageOnFilterChange = false,
    serializeDateFilterValues = false,
    clearStaleSearchParams = false,
  }: {
    pagination: boolean | undefined;
    initialPageSize: number;
    preventUrlReadWhenSyncDisabled?: boolean;
    /** Snap back to page 0 when the filter model changes (opt-in). */
    resetPageOnFilterChange?: boolean;
    /** Serialize Date filter values as ISO dates in the URL (opt-in). */
    serializeDateFilterValues?: boolean;
    /**
     * Drop the DataGrid-owned URL params this grid's current models no longer
     * produce, so a cleared filter or sort doesn't leave a stale value that
     * `useDataGridInitialState` reads back as initial state (opt-in).
     *
     * The params are not namespaced per grid, so only enable this on a route
     * where this grid is the sole owner of them — a second sync-enabled grid on
     * the same route would have its params deleted by this one.
     */
    clearStaleSearchParams?: boolean;
  }
) => {
  const [, setSearchParams] = useSearchParams();

  // extract initial state from URL and props
  const _computedInitialState = useDataGridInitialState(initialState, {
    pagination,
    initialPageSize,
  });

  // When preventUrlReadWhenSyncDisabled is true, override URL pagination with initialState or default to page 0
  const initialPaginationModel = preventUrlReadWhenSyncDisabled
    ? initialState?.pagination?.paginationModel ||
      (pagination
        ? {
            page: 0,
            pageSize: initialPageSize,
          }
        : undefined)
    : _computedInitialState?.pagination?.paginationModel;

  // Initialize paginationModel, sortModel, and filterModel from initialState or URL
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel | undefined>(
    initialPaginationModel as GridPaginationModel
  );
  // When preventUrlReadWhenSyncDisabled is true, use initialState sorting/filtering instead of URL
  const initialSortModel = preventUrlReadWhenSyncDisabled
    ? initialState?.sorting?.sortModel || _computedInitialState?.sorting?.sortModel
    : _computedInitialState?.sorting?.sortModel;
  const initialFilterModel = preventUrlReadWhenSyncDisabled
    ? initialState?.filter?.filterModel || _computedInitialState?.filter?.filterModel
    : _computedInitialState?.filter?.filterModel;
  const [sortModel, setSortModel] = useState<GridSortModel | undefined>(
    initialSortModel as GridSortModel
  );
  const [filterModel, setFilterModel] = useState<GridFilterModel | undefined>(
    initialFilterModel as GridFilterModel
  );

  // sync search params with state, update pageSize if pageSize not in pageSizeOptions
  useEffect(() => {
    // check if the pageSize is available in pageSizeOptions
    const isPageSizeAvailable = !!pageSizeOptions.find(
      (option) => option === paginationModel?.pageSize
    );

    if (pagination && paginationModel?.pageSize && !isPageSizeAvailable) {
      // get the first pageSizeOption value
      const firstPageSizeOptionValue =
        typeof pageSizeOptions[0] === 'number' ? pageSizeOptions[0] : pageSizeOptions[0].value;

      // if check if the initialPageSize is not in pageSizeOptions
      const isInitialPageSizeInOptions = pageSizeOptions.find((option) =>
        typeof option === 'number' ? option === initialPageSize : option.value === initialPageSize
      );

      // set pageSize to initialPageSize if it is in pageSizeOptions, otherwise set it to the first pageSizeOption value
      const _pageSize = isInitialPageSizeInOptions ? initialPageSize : firstPageSizeOptionValue;
      // update paginationModel state
      setPaginationModel((prev) => {
        return {
          page: prev?.page || 0,
          pageSize: _pageSize,
        };
      });
    }
  }, [initialPageSize, pageSizeOptions, pagination, paginationModel?.pageSize]);

  useEffect(() => {
    if (!disableSearchParamSync && ((pagination && paginationModel) || sortModel || filterModel)) {
      const queryString = buildQueryString({
        paginationModel,
        sortModel,
        filterModel,
        serializeDateFilterValues,
      });
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        const queryParams = new URLSearchParams(queryString);

        if (clearStaleSearchParams) {
          DATA_GRID_SEARCH_PARAMS.forEach((key) => {
            if (!queryParams.has(key)) {
              newParams.delete(key);
            }
          });
        }

        // Merge query params into new params
        queryParams.forEach((value, key) => {
          newParams.set(key, value);
        });
        return newParams;
      });
    }
  }, [
    pagination,
    disableSearchParamSync,
    clearStaleSearchParams,
    paginationModel,
    sortModel,
    filterModel,
    setSearchParams,
  ]);

  // Opt-in: filtering changes the result set — snap back to the first page so a
  // grid sitting on page N can't strand on an out-of-range page (server mode
  // gets no automatic reset from MUI). Default off to keep legacy behavior.
  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);
      if (resetPageOnFilterChange) {
        setPaginationModel((prev) => (prev && prev.page !== 0 ? { ...prev, page: 0 } : prev));
      }
    },
    [resetPageOnFilterChange]
  );

  return {
    initialState: _computedInitialState,
    handlePaginationModelChange: setPaginationModel,
    handleSortModelChange: setSortModel,
    handleFilterModelChange,
    paginationModel: pagination ? paginationModel : undefined,
    sortModel,
    filterModel,
  };
};
